import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    ButtonGroup,
    Divider,
    Chip,
    useTheme,
    CircularProgress,
    Stack
} from '@mui/material';
import {
    Save,
    CheckCircle,
    ArrowBack,
    ArrowForward,
    QuestionMark,
    CheckCircleOutline,
    School,
    Psychology,
    Article
} from '@mui/icons-material';
import { tokens } from '../../../../theme';
import ReviewForm from './ReviewForm';
import QuestionRender from '../../../../components/QuestionRender';

const QuestionReviewPanel = ({
    question,
    questionNumber,
    totalQuestions,
    review,
    onSaveReview,
    onNext,
    onPrevious,
    saving
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [formData, setFormData] = useState({
        status: '',
        feedback: '',
        error_type: '',
        error_categories: [],
        suggested_correction: '',
        severity: '',
        confidence_level: 3
    });
    const [hasChanges, setHasChanges] = useState(false);

    // Load existing review data
    useEffect(() => {
        if (review) {
            setFormData({
                status: review.status || '',
                feedback: review.feedback || '',
                error_type: review.error_type || '',
                error_categories: review.error_categories || [],
                suggested_correction: review.suggested_correction || '',
                severity: review.severity || '',
                confidence_level: review.confidence_level || 3
            });
            setHasChanges(false);
        } else {
            setFormData({
                status: '',
                feedback: '',
                error_type: '',
                error_categories: [],
                suggested_correction: '',
                severity: '',
                confidence_level: 3
            });
            setHasChanges(false);
        }
    }, [review, question]);

    const handleFormChange = (updates) => {
        setFormData(prev => ({ ...prev, ...updates }));
        setHasChanges(true);
    };

    const handleSave = async () => {
        await onSaveReview(formData);
        setHasChanges(false);
    };

    const handleSaveAndNext = async () => {
        await handleSave();
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (onNext) {
            setTimeout(onNext, 100);
        }
    };

    if (!question) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography>No question data available</Typography>
            </Box>
        );
    }

    const questionData = question.question_data;
    const isReviewed = !!review || formData.status;

    // Parse question content based on type
    const renderQuestionContent = () => {
        if (!questionData) return null;

        return (
            <Stack spacing={3}>
                {/* Passage */}
                {questionData.passage && (
                    <Paper elevation={2} sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <Article fontSize="small" />
                            <Typography variant="h6" fontWeight="bold">
                                Passage
                            </Typography>
                        </Box>
                        <QuestionRender
                            content={questionData.passage || ''}
                            highlightId={questionData?.highlight_id ?? null}
                        />
                    </Paper>
                )}

                {/* Question Text */}
                <Paper elevation={2} sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <QuestionMark fontSize="small" />
                        <Typography variant="h6" fontWeight="bold">
                            Question
                        </Typography>
                    </Box>
                    <QuestionRender content={questionData.content || ''} />
                </Paper>

                {/* Options */}
                {questionData.answer_type === 'option' && questionData.options && (
                    <Paper elevation={2} sx={{ p: 2 }}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Answer Choices
                        </Typography>
                        <Stack spacing={1}>
                            {Object.entries(questionData.options).map(([key, value], index) => {
                                const isCorrect = questionData.answer === key;
                                const optionLabel = String.fromCharCode(65 + index); // Convert to A, B, C, D
                                return (
                                    <Box
                                        key={key}
                                        sx={{
                                            p: 1.5,
                                            border: isCorrect ? `2px solid ${colors.greenAccent[400]}` : `1px solid ${colors.grey[700]}`,
                                            borderRadius: 1
                                        }}
                                    >
                                        <Box display="flex" alignItems="flex-start" gap={1}>
                                            <Chip 
                                                label={optionLabel}
                                                size="small"
                                                color={isCorrect ? "success" : "default"}
                                                sx={{ minWidth: 32 }}
                                            />
                                            <Box flex={1}>
                                                <QuestionRender content={value.content || ''} />
                                            </Box>
                                            {isCorrect && (
                                                <CheckCircleOutline 
                                                    sx={{ 
                                                        color: colors.greenAccent[400],
                                                        fontSize: 20
                                                    }} 
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Paper>
                )}

                {/* Correct Answer */}
                <Paper elevation={2} sx={{ p: 2, border: `1px solid ${colors.greenAccent[400]}` }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <CheckCircleOutline color="success" />
                        <Typography variant="h6" fontWeight="bold">
                            Correct Answer(s)
                        </Typography>
                        {questionData.answer_type === 'option' ? (
                            <Chip 
                                label={
                                    // Convert answer key to letter (1->A, 2->B, etc.)
                                    questionData.answer ? 
                                        String.fromCharCode(65 + (parseInt(questionData.answer))) : 
                                        'Not specified'
                                }
                                color="success"
                                size="medium"
                            />
                            ) : questionData.answer_type === 'typing' ? (
                                questionData.answer?.split('|')?.map((ans, idx) => (
                                    <Chip 
                                        key={idx}
                                        label={ans.trim() || 'Empty'} 
                                        color="success"
                                        size="medium"
                                        sx={{ mr: 1, mb: 1 }}
                                    />
                                ))
                            ) : null}
                    </Box>
                </Paper>

                {/* Explanation */}
                {questionData.explanation && (
                    <Paper elevation={2} sx={{ p: 2 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <School fontSize="small" />
                            <Typography variant="h6" fontWeight="bold">
                                Explanation
                            </Typography>
                        </Box>
                        <Box sx={{ p: 2 }}>
                            <QuestionRender content={questionData.explanation} />
                        </Box>
                    </Paper>
                )}

                {/* Metadata */}
                <Box display="flex" gap={1} flexWrap="wrap">
                    {questionData.difficulty && (
                        <Chip 
                            icon={<Psychology />}
                            label={`Difficulty: ${
                                questionData.difficulty === 1 ? 'Easy' :
                                questionData.difficulty === 2 ? 'Medium' :
                                questionData.difficulty === 3 ? 'Hard' :
                                questionData.difficulty
                            }`} 
                            size="small"
                            variant="outlined"
                            color="info"
                        />
                    )}
                </Box>
            </Stack>
        );
    };

    return (
        <Box height="100%" display="flex" flexDirection="column">
            {/* Header */}
            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h5" fontWeight="bold">
                            Question {questionNumber}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            of {totalQuestions}
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                        {isReviewed && (
                            <Chip 
                                label="Reviewed" 
                                color="success" 
                                icon={<CheckCircle />}
                                size="medium"
                            />
                        )}
                        {question.is_required && (
                            <Chip 
                                label="Required" 
                                color="warning"
                                variant="filled"
                                size="medium"
                            />
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* Question Content - Scrollable */}
            <Box flex={1} overflow="auto" pb={2}>
                <Box 
                    sx={{ 
                        maxWidth: '1000px', 
                        margin: '0 auto',
                        minWidth: '800px',
                    }}
                >
                    {renderQuestionContent()}
                    
                    <Divider sx={{ my: 4 }} />
                    
                    {/* Review Form */}
                    <Paper elevation={2} sx={{ p: 2 }}>
                        <ReviewForm
                            formData={formData}
                            onChange={handleFormChange}
                            hasError={formData.status === 'has_error' || formData.status === 'needs_revision'}
                        />
                    </Paper>
                </Box>
            </Box>

            {/* Action Buttons */}
            <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={onPrevious}
                        disabled={!onPrevious || saving}
                    >
                        Previous
                    </Button>

                    <ButtonGroup variant="contained">
                        <Button
                            color="primary"
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                            onClick={handleSave}
                            disabled={!formData.status || saving || !hasChanges}
                        >
                            {saving ? 'Saving...' : 'Save Review'}
                        </Button>
                        
                        {onNext && (
                            <Button
                                color="success"
                                onClick={handleSaveAndNext}
                                disabled={!formData.status || saving}
                            >
                                Save & Next
                            </Button>
                        )}
                    </ButtonGroup>

                    <Button
                        variant="outlined"
                        endIcon={<ArrowForward />}
                        onClick={onNext}
                        disabled={!onNext || saving}
                    >
                        Next
                    </Button>
                </Stack>
            </Paper>
        </Box>
    );
};

export default QuestionReviewPanel;